import React, { ChangeEvent, useRef, useState, useEffect } from 'react';
import { Chip, Button, Alert } from '@mui/material';

import StyledEmailsInput from './styles';
import ViewMoreIcon from '../../assets/icons/view-more.svg';
import ControlledInput from '../ControlledInput';
import CustomButton from '../CustomButton';

interface IMultipleEmailsInputProps {
  emails: string[];
  setEmails: CallableFunction;
  errorMessage: string;
  setErrorMessage: CallableFunction;
  placeholder: string;
  label: string;
  maxLen?: number;
  classes?: string;
  style?: any;
  required?: boolean;
}

const MultipleEmailsInput: React.FC<IMultipleEmailsInputProps> = ({
  emails,
  setEmails,
  maxLen,
  classes,
  style,
  errorMessage,
  setErrorMessage,
  placeholder,
  label,
  required
}) => {
  const [email, setEmail] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [viewMoreBtnVisible, setViewMoreBtnVisible] = useState(false);
  const overflowingText = useRef<HTMLDivElement | null>(null);

  const checkEmailsOverflow = (container: HTMLDivElement | null) => {
    if (container) {
      if (expanded) {
        setViewMoreBtnVisible(container.offsetHeight > 100);
      } else {
        setViewMoreBtnVisible(container.offsetHeight < container.scrollHeight);
      }
    }
  };

  const validateEmail = (e: string) => {
    const validateEmailRegEx =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

    return validateEmailRegEx.test(e);
  };

  const addEmail = () => {
    const newEmails = email.split(/,+/).filter(Boolean);
    const invalidEmails = newEmails.filter((e) => e && !validateEmail(e));

    setEmails([
      ...emails,
      ...newEmails.filter(
        (e: string) => !invalidEmails.includes(e) && !emails.includes(e)
      )
    ]);

    if (invalidEmails.length) {
      setErrorMessage(`Email(s) ${invalidEmails.join(', ')} not valid.`);
    } else {
      setErrorMessage('');
    }

    setEmail('');
  };

  const removeEmail = (e: string) => {
    setEmails(emails.filter((item) => item !== e));
  };

  useEffect(() => {
    checkEmailsOverflow(overflowingText.current);
  }, [emails]);

  return (
    <StyledEmailsInput expanded={expanded} style={style}>
      <div className='multiple-emails'>
        <div className='multiple-emails__input'>
          <ControlledInput
            id='emails'
            placeholder={placeholder}
            value={email}
            handleChange={(event: ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value.trim())
            }
            maxLen={maxLen}
            label={label}
            type='text'
            classes='label--w-100'
            required={required}
          />
          <CustomButton
            type='button'
            classes='btn--w-10 btn--blue'
            title='Add'
            disabled={!email.trim()}
            handleClick={() => addEmail()}
          />
        </div>
        {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}
      </div>
      <div ref={overflowingText} className={`emails-container ${classes}`}>
        {emails.map((e: string) => (
          <Chip key={e} label={e} onDelete={() => removeEmail(e)} />
        ))}
      </div>
      {viewMoreBtnVisible && (
        <Button
          onClick={() => setExpanded(!expanded)}
          className={`view-more-button ${classes}`}
        >
          <span style={{ color: 'white' }}>
            {expanded ? 'View less' : 'View more'}
          </span>
          <img src={ViewMoreIcon} alt='ViewMore' />
        </Button>
      )}
    </StyledEmailsInput>
  );
};

MultipleEmailsInput.defaultProps = {
  maxLen: 100,
  classes: '',
  style: null,
  required: false
};

export default MultipleEmailsInput;
